import SectionHeading from "@/components/base/SectionHeading";

export interface CareStep {
  step: string;
  title: string;
  text: string;
  icon: string;
}

interface CareStepsProps {
  eyebrow: string;
  title: string;
  description?: string;
  steps: CareStep[];
  tone?: "light" | "tinted";
}

/**
 * Numbered patient-journey timeline with a subtle connecting line.
 * Horizontal on desktop, stacked vertically on small screens.
 */
export default function CareSteps({ eyebrow, title, description, steps, tone = "light" }: CareStepsProps) {
  const background = tone === "tinted" ? "bg-background-100" : "bg-background-50";

  return (
    <section className={`${background} py-16 md:py-24`}>
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading eyebrow={eyebrow} title={title} description={description} />

        <div className="relative mt-14">
          <div
            className="absolute left-1/2 top-7 hidden h-px w-[calc(100%-6rem)] -translate-x-1/2 bg-background-300 lg:block"
            aria-hidden="true"
          ></div>

          <ol className="relative grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-5 lg:gap-5">
            {steps.map((step, index) => (
              <li key={step.step} className="reveal relative flex flex-col items-start lg:items-center lg:text-center">
                <span className="relative flex h-14 w-14 items-center justify-center rounded-full border border-background-300 bg-background-50 text-primary-700">
                  <i className={`${step.icon} text-2xl`} aria-hidden="true"></i>
                  <span className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-accent-500 font-label text-[0.65rem] font-bold text-background-50">
                    {step.step}
                  </span>
                </span>
                <h3 className="mt-5 font-heading text-base font-semibold text-foreground-950">{step.title}</h3>
                <p className="mt-2 max-w-xs font-body text-sm leading-relaxed text-foreground-600">{step.text}</p>
                {index < steps.length - 1 ? (
                  <span className="mt-5 lg:hidden" aria-hidden="true">
                    <i className="ri-arrow-down-line text-xl text-background-400"></i>
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}